// ApiPerformanceGraphScreen.jsx
import React, { useState } from "react";
import {
    Box,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { ScatterChart } from "@mui/x-charts/ScatterChart";
import { BarChart } from "@mui/x-charts/BarChart";
import {
    useGetApiPerformanceSpikesQuery,
    useGetApiPerformanceFrequencyQuery,
} from "store/services/apiPerformanceLogApi";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

export const ApiPerformanceGraphScreen = () => {
    const breadcrumbs = [
        { title: "Reports", path: "/app/crm/reports/list" },
        { title: "API Performance Graph" },
    ];

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const { data: spikeData, isFetching: isSpikeFetching } =
        useGetApiPerformanceSpikesQuery({
            startDate: startDate || undefined,
            endDate: endDate || undefined,
        });

    const { data: frequencyData, isFetching: isFrequencyFetching } =
        useGetApiPerformanceFrequencyQuery({
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            limit: 10,
        });

    const spikes = spikeData?.data || [];
    const frequency = frequencyData?.data || [];

    // Split into two series so warn/critical render as distinguishable colors
    const warnPoints = spikes
        .filter((p) => p.level === "warn")
        .map((p, idx) => ({
            x: new Date(p.createdAt).getTime(),
            y: p.duration,
            id: `warn-${idx}`,
        }));
    const criticalPoints = spikes
        .filter((p) => p.level === "critical")
        .map((p, idx) => ({
            x: new Date(p.createdAt).getTime(),
            y: p.duration,
            id: `critical-${idx}`,
        }));

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs}>
            <BreadcrumbLayout.Paper>
                <Box sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        {/* Filters */}
                        <Grid item xs={12}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={3} sx={{ mb: 2, mr: { sm: 2 } }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Start Date"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3} sx={{ mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="End Date"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                            <Typography variant="caption" color="text.secondary">
                                Defaults to the last 7 days when no dates are selected.
                            </Typography>
                        </Grid>

                        {/* Spike graph: duration vs time */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Response Time Spikes
                            </Typography>
                            <ScatterChart
                                height={320}
                                loading={isSpikeFetching}
                                series={[
                                    {
                                        label: "Warn",
                                        data: warnPoints,
                                        color: "#f39c12",
                                    },
                                    {
                                        label: "Critical",
                                        data: criticalPoints,
                                        color: "#e74c3c",
                                    },
                                ]}
                                xAxis={[
                                    {
                                        label: "Time",
                                        valueFormatter: (value) =>
                                            new Date(value).toLocaleString(),
                                    },
                                ]}
                                yAxis={[{ label: "Duration (ms)" }]}
                            />
                        </Grid>

                        {/* Frequency graph: which endpoint appears most */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Most Frequent Slow Endpoints
                            </Typography>
                            <BarChart
                                height={320}
                                loading={isFrequencyFetching}
                                dataset={frequency}
                                xAxis={[{ scaleType: "band", dataKey: "endpoint" }]}
                                series={[{ dataKey: "count", label: "Slow Hits", color: "#3498db" }]}
                            />
                        </Grid>

                        {/* Frequency table: same data as the bar chart, in tabular form */}
                        <Grid item xs={12}>
                            <TableContainer
                                component={Paper}
                                variant="outlined"
                                sx={{ borderRadius: 0, borderColor: "divider", bgcolor: "background.paper" }}
                            >
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "action.hover" }}>
                                            <TableCell sx={{ fontWeight: 700, borderColor: "divider" }}>Endpoint</TableCell>
                                            <TableCell sx={{ fontWeight: 700, borderColor: "divider" }} align="right">Slow Hits</TableCell>
                                            <TableCell sx={{ fontWeight: 700, borderColor: "divider" }} align="right">Avg Duration</TableCell>
                                            <TableCell sx={{ fontWeight: 700, borderColor: "divider" }} align="right">Max Duration</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {frequency.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" sx={{ borderColor: "divider" }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        No data for this range.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {frequency.map((row) => (
                                            <TableRow key={row.endpoint}>
                                                <TableCell sx={{ borderColor: "divider", fontFamily: "monospace", fontSize: "0.75rem" }}>
                                                    {row.endpoint}
                                                </TableCell>
                                                <TableCell sx={{ borderColor: "divider" }} align="right">{row.count}</TableCell>
                                                <TableCell sx={{ borderColor: "divider" }} align="right">{row.avgDuration} ms</TableCell>
                                                <TableCell sx={{ borderColor: "divider" }} align="right">{row.maxDuration} ms</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </Box>
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};